import React, { useState, useCallback, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import {
    Typography,
    TextField,
    Alert,
    Grid,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress
} from '@mui/material';
import { Article as ArticleIcon } from '@mui/icons-material';

const AuthSetupStep = ({
    authConfig,
    setAuthConfig,
    analyzeLoginMutation,
    testConnectionMutation,
    testConnectionResult,
    analyzeLoginResult,
    isTestingConnection,
    isAnalyzingLogin,
    eshopId,
    htmlSize,
    setHtmlSize,
    iterations,
    setIterations
}, ref) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Track the last test result ID to detect new tests
    const lastTestResultRef = useRef(null);
    // Track if modal was manually closed for current result
    const wasManuallyClosedRef = useRef(false);

    // Simple function to open modal
    const handleOpenPreview = useCallback(() => {
        setIsModalOpen(true);
        wasManuallyClosedRef.current = false;
    }, []);

    // Simple function to close modal
    const handleClosePreview = useCallback(() => {
        setIsModalOpen(false);
        wasManuallyClosedRef.current = true;
    }, []);

    // Expose the handleOpenPreview function to the parent component
    useImperativeHandle(ref, () => ({
        handleOpenPreview
    }));

    // Auto-open modal when NEW test results arrive (mirrors SelectorConfigStep behavior)
    useEffect(() => {
        if (testConnectionResult) {
            const currentResultId = JSON.stringify(testConnectionResult);

            // Only open if this is a genuinely new test result AND wasn't manually closed
            if (currentResultId !== lastTestResultRef.current) {
                lastTestResultRef.current = currentResultId;
                wasManuallyClosedRef.current = false;
                setIsModalOpen(true);
            } else if (!wasManuallyClosedRef.current) {
                // If it's the same result but wasn't manually closed, keep it closed
                setIsModalOpen(false);
            }
        }
    }, [testConnectionResult]);

    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Login URL"
                    value={authConfig.login_url || ''}
                    onChange={(e) => setAuthConfig({ ...authConfig, login_url: e.target.value })}
                    helperText="URL of the login page"
                    size="small"
                />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                    label="Username Field Selector"
                    value={authConfig.username_field || ''}
                    onChange={(e) => setAuthConfig({ ...authConfig, username_field: e.target.value })}
                    helperText="CSS selector for the username input field"
                    size="small"
                    sx={{ flex: 1, minWidth: '200px' }}
                />
                <TextField
                    label="Password Field Selector"
                    value={authConfig.password_field || ''}
                    onChange={(e) => setAuthConfig({ ...authConfig, password_field: e.target.value })}
                    helperText="CSS selector for the password input field"
                    size="small"
                    sx={{ flex: 1, minWidth: '200px' }}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Login Button Selector"
                    value={authConfig.login_button || ''}
                    onChange={(e) => setAuthConfig({ ...authConfig, login_button: e.target.value })}
                    helperText="CSS selector for the login button"
                    size="small"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Modal Container Selector (Optional)"
                    value={authConfig.modal_container || ''}
                    onChange={(e) => setAuthConfig({ ...authConfig, modal_container: e.target.value })}
                    helperText="CSS selector for modal container if login form is in a popup (e.g., .modal, .popup, #login-modal)"
                    size="small"
                />
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    Test Credentials
                </Typography>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                    label="Test Username"
                    value={authConfig.test_credentials?.username || ''}
                    onChange={(e) => setAuthConfig({
                        ...authConfig,
                        test_credentials: {
                            ...authConfig.test_credentials,
                            username: e.target.value
                        }
                    })}
                    helperText="Username for testing the connection"
                    size="small"
                    sx={{ flex: 1, minWidth: '200px' }}
                />
                <TextField
                    label="Test Password"
                    type="password"
                    value={authConfig.test_credentials?.password || ''}
                    onChange={(e) => setAuthConfig({
                        ...authConfig,
                        test_credentials: {
                            ...authConfig.test_credentials,
                            password: e.target.value
                        }
                    })}
                    helperText="Password for testing the connection"
                    size="small"
                    sx={{ flex: 1, minWidth: '200px' }}
                />
            </Grid>
            <Grid item xs={12}>
                {analyzeLoginResult && (
                    <Alert
                        severity={analyzeLoginResult.success ? 'success' : 'error'}
                        sx={{ mb: 2 }}
                    >
                        {analyzeLoginResult.message}
                    </Alert>
                )}
                {testConnectionResult && (
                    <Alert
                        severity={testConnectionResult.success ? 'success' : 'error'}
                        sx={{ mb: 2 }}
                    >
                        {testConnectionResult.message}
                    </Alert>
                )}
            </Grid>
            {/* Show log button for test connection results */}
            {/* Buttons are now handled in the main EshopWizard component */}

            {/* Preview Dialog - Simplified */}
            <Dialog
                open={isModalOpen}
                onClose={handleClosePreview}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Test Connection Log</DialogTitle>
                <DialogContent>
                    {testConnectionResult ? (
                        <Box>
                            {testConnectionResult.success ? (
                                <Box>
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        {testConnectionResult.message}
                                    </Alert>
                                    {/* Append scraper logs if present */}
                                    {(testConnectionResult.logs || testConnectionResult.structured_logs) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Connection Test Logs
                                            </Typography>
                                            <Box component="pre" sx={{ background: '#f5f5', p: 1, maxHeight: 900, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                                {testConnectionResult.logs
                                                    ? testConnectionResult.logs
                                                    : JSON.stringify(testConnectionResult.structured_logs, null, 2)}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box>
                                    <Alert severity="error">
                                        {testConnectionResult.message}
                                    </Alert>
                                    {/* Append scraper logs if present */}
                                    {(testConnectionResult.logs || testConnectionResult.structured_logs) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Connection Test Logs
                                            </Typography>
                                            <Box component="pre" sx={{ background: '#f5f5', p: 1, maxHeight: 900, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                                {testConnectionResult.logs
                                                    ? testConnectionResult.logs
                                                    : JSON.stringify(testConnectionResult.structured_logs, null, 2)}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={handleClosePreview}>Close</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default forwardRef(AuthSetupStep);